/* Rozvaha klienta jako obrázek — co má, co dluží, co mu doopravdy zbývá.
 *
 * Zadání majitele: „graf nebo tak něco ukazuje jeho majetek."
 * Přestavba 20. 8. 2026: „jeho majetek neumíme udělat vizuál hezčí? grafy, animace?"
 *
 * ⚠️ NEJDŮLEŽITĚJŠÍ ZMĚNA NENÍ KOSMETICKÁ — BARVY LHALY.
 * Do 8/2026 měl nefinanční majetek `var(--accent)` (terakota) a dluhy
 * `var(--cat-c)` (červená). Na obrazovce to byla skoro tatáž barva, takže
 * nemovitost za 5,8 mil. vypadala stejně jako dluh 2 mil. Klient sedí u stolu
 * a červenou čte jako „problém" — přitom je to to nejcennější, co má.
 * Teď: majetek nikdy nemá dluhovou barvu. Zelená = finanční, tyrkysová =
 * nemovitosti a věci, červená JEN dluh.
 *
 * PROČ SKLÁDANÝ PRUH A NE KOLÁČ: klient má porovnat DVĚ VELIČINY proti sobě
 * (co vlastní × co dluží). Na společné ose je vidět poměr na první pohled
 * i z projektoru; koláč to neumí, protože dluh do majetku nepatří.
 *
 * Čísla NEPOČÍTÁ — bere je z lib/crm/rozvaha.js, aby existovala jen jedna
 * definice čistého jmění (stejná jako v lib/bilance.js).
 */
import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { podleDruhu, podil } from "@/lib/crm/rozvaha";
import { druhLabel } from "@/lib/analyzaV2/nefinancni";
import { pluralJazyka, textJazyka } from "@/lib/i18n/textMimoReact";
import { localeJazyka } from "@/lib/i18n/format";

/* Majetek NIKDY nemá dluhovou barvu — viz hlavička. */
const BARVA = {
  financni: "var(--positive)",
  nefinancni: "var(--info)",
  dluhy: "var(--cat-c)",
};

/* Číslo, které dopočítá nahoru. Bez knihovny — jeden requestAnimationFrame.
   Respektuje „omezit pohyb": kdo má animace vypnuté, vidí rovnou výsledek. */
function useNaskoc(cil, bezPohybu) {
  const [v, setV] = useState(bezPohybu ? cil : 0);
  const ref = useRef();
  useEffect(() => {
    if (bezPohybu) { setV(cil); return undefined; }
    const zacatek = performance.now();
    const TRVANI = 900;
    const krok = (t) => {
      const p = Math.min(1, (t - zacatek) / TRVANI);
      const e = 1 - Math.pow(1 - p, 3);          // ease-out, ať to na konci dosedne
      setV(cil * e);
      if (p < 1) ref.current = requestAnimationFrame(krok);
    };
    ref.current = requestAnimationFrame(krok);
    return () => cancelAnimationFrame(ref.current);
  }, [cil, bezPohybu]);
  return v;
}

/* Jeden díl skládaného pruhu. */
const Dil = ({ sirka, barva, zpozdeni, bezPohybu, titulek }) => (
  <motion.div
    initial={bezPohybu ? false : { width: 0 }}
    animate={{ width: `${sirka}%` }}
    transition={bezPohybu ? { duration: 0 } : { duration: 0.8, delay: zpozdeni, ease: [0.16, 1, 0.3, 1] }}
    style={{ height: "100%", background: barva }}
    title={titulek}
  />
);

/* `castka === null` = v evidenci nic není → šedá „—“ bez procenta
   (nezadané ≠ nula, ověření 4. 10. 2026 — lišta analýzy píše totéž). */
const Legenda = ({ barva, popis, castka, procent, tid }) => (
  <span className="inline-flex items-baseline gap-1.5 text-maly">
    <span style={{ width: 9, height: 9, borderRadius: 3, background: barva, display: "inline-block", flexShrink: 0 }} />
    <span style={{ color: "var(--muted)" }}>{popis}</span>
    <b className="tabular-nums" data-testid={tid} style={{ color: castka === null ? "var(--faint)" : "var(--ink)" }}>{castka === null ? "—" : castka}</b>
    {castka !== null && procent !== null && <span className="tabular-nums" style={{ color: "var(--faint)" }}>{procent} %</span>}
  </span>
);

export default function RozvahaGraf({ r, fmtMoney, lang = "cs" }) {
  /* Popisky jdou přes pětijazyčný slovník. Dřív tu byl `const sk = lang === "sk"`
   * a dvanáct ternárů `sk ? … : …` — ty znaly jen češtinu a slovenštinu, takže
   * Vietnamec i Ukrajinec četli rozvahu česky. Komponenta není v Reactím
   * kontextu jazyka (dostává `lang` propem), proto `textJazyka`, ne hook. */
  const t = (klic, params) => textJazyka(lang, `rozvahaGraf.${klic}`, undefined, params);
  const bezPohybu = useReducedMotion();
  const cisteCil = r && !r.prazdna ? r.cisteJmeni : 0;
  const naskoc = useNaskoc(cisteCil, bezPohybu);

  if (!r || r.prazdna) return null;

  const fmt = (v) => (typeof fmtMoney === "function" ? fmtMoney(v) : `${Math.round(v).toLocaleString(localeJazyka(lang))} Kč`);
  const druhy = podleDruhu(r);
  const zaporne = r.cisteJmeni < 0;

  /* Společné měřítko pro OBA pruhy — jinak by dluh vypadal stejně velký
     jako majetek a poměr, o který tu jde, by zmizel. */
  const max = Math.max(r.majetekCelkem, r.dluhy, 1);
  const pct = (x) => (max > 0 ? (x / max) * 100 : 0);
  const procentZMajetku = (x) => (r.majetekCelkem > 0 ? Math.round((x / r.majetekCelkem) * 100) : null);
  /* NEZADANÉ ≠ NULA (ověření 4. 10. 2026): klientka s penězi jen na účtech
     měla na kartě „Co dluží 0 Kč“ červeně a „Nemovitosti a věci 0 Kč 0 %“,
     lišta analýzy u téhož klienta píše „—“. Číslo jen u toho, co v evidenci je. */
  const ma = (k) => (r.polozky?.[k]?.length || 0) > 0;
  const maMajetek = ma("financni") || ma("nefinancni");

  return (
    <div
      className="mb-4"
      style={{
        background: "var(--surface)", border: "1px solid var(--line)",
        borderRadius: 14, padding: "20px 22px", boxShadow: "var(--shadow-card)",
      }}
      data-testid="rozvaha-graf"
    >
      {/* ── ČISTÉ JMĚNÍ — výsledek, ne vstup. Proto nahoře a největší. ── */}
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
        <span className="text-popisek font-bold uppercase tracking-wider" style={{ color: "var(--muted)" }}>
          {t("cisteJmeni")}
        </span>
        <span
          className="text-[30px] font-bold tabular-nums leading-none"
          style={{ color: zaporne ? "var(--cat-c)" : "var(--positive)", fontFamily: "var(--font-display)" }}
          data-testid="rozvaha-ciste-jmeni"
        >
          {fmt(naskoc)}
        </span>
      </div>
      <div className="text-[12px] mb-5" style={{ color: "var(--faint)" }}>
        {t("cisteJmeniPod")}
      </div>

      {/* ── CO VLASTNÍ — jeden skládaný pruh, ať je vidět složení ── */}
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-text font-semibold" style={{ color: "var(--ink)" }}>
          {t("coVlastni")}
        </span>
        <span className="text-[15px] font-bold tabular-nums" style={{ color: "var(--ink)" }}
          data-testid="rozvaha-majetek-celkem">
          {maMajetek ? fmt(r.majetekCelkem) : "—"}
        </span>
      </div>
      <div className="flex" style={{ height: 18, borderRadius: 9, background: "var(--surface-2)", overflow: "hidden" }}>
        <Dil sirka={pct(r.financni)} barva={BARVA.financni} zpozdeni={0} bezPohybu={bezPohybu}
          titulek={`${t("financni")}: ${fmt(r.financni)}`} />
        <Dil sirka={pct(r.nefinancni)} barva={BARVA.nefinancni} zpozdeni={0.12} bezPohybu={bezPohybu}
          titulek={`${t("nefinancni")}: ${fmt(r.nefinancni)}`} />
      </div>
      <div className={`flex flex-wrap gap-x-4 gap-y-1 mt-2 ${r.budouci > 0 ? "mb-1.5" : "mb-5"}`}>
        <Legenda barva={BARVA.financni} popis={t("financni")} tid="rozvaha-financni"
          castka={ma("financni") ? fmt(r.financni) : null} procent={procentZMajetku(r.financni)} />
        <Legenda barva={BARVA.nefinancni} popis={t("nefinancni")} tid="rozvaha-nefinancni"
          castka={ma("nefinancni") ? fmt(r.nefinancni) : null} procent={procentZMajetku(r.nefinancni)} />
      </div>
      {/* Dědictví / dar = budoucí majetek: pod součtem, do jmění se nepočítá
          (stejně jako analýza a její tisk — lib/bilance.js, lib/crm/rozvaha.js). */}
      {r.budouci > 0 && (
        <div className="text-drobny mb-5" style={{ color: "var(--muted)" }} data-testid="rozvaha-budouci">
          {textJazyka(lang, "planPojistne.budouci", undefined, { castka: fmt(r.budouci) })}
        </div>
      )}

      {/* ── CO DLUŽÍ — stejné měřítko, aby byl poměr pravdivý ── */}
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-text font-semibold" style={{ color: "var(--ink)" }}>
          {t("coDluzi")}
        </span>
        {/* Červená jen pro dluh, který v evidenci je — „—“ je šedá. */}
        <span className="text-[15px] font-bold tabular-nums" style={{ color: ma("dluhy") ? BARVA.dluhy : "var(--faint)" }}
          data-testid="rozvaha-dluhy">
          {ma("dluhy") ? fmt(r.dluhy) : "—"}
        </span>
      </div>
      <div style={{ height: 18, borderRadius: 9, background: "var(--surface-2)", overflow: "hidden" }}>
        <Dil sirka={pct(r.dluhy)} barva={BARVA.dluhy} zpozdeni={0.24} bezPohybu={bezPohybu}
          titulek={`${t("zavazky")}: ${fmt(r.dluhy)}`} />
      </div>
      {r.dluhy > 0 && procentZMajetku(r.dluhy) !== null && (
        <div className="text-[12px] mt-1.5" style={{ color: "var(--muted)" }} data-testid="rozvaha-podil-dluhu">
          {t("podilDluhu", { pct: procentZMajetku(r.dluhy) })}
        </div>
      )}

      {/* ── Z ČEHO SE SKLÁDÁ VĚCNÝ MAJETEK ── */}
      {druhy.length > 0 && (
        <div className="mt-5 pt-3" style={{ borderTop: "1px solid var(--line)" }}>
          <div className="text-popisek font-bold uppercase tracking-wider mb-2" style={{ color: "var(--muted)" }}>
            {t("vecnyMajetek")}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {druhy.map((d, i) => (
              <motion.span
                key={d.druh}
                initial={bezPohybu ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={bezPohybu ? { duration: 0 } : { duration: 0.35, delay: 0.4 + i * 0.05 }}
                className="inline-flex items-baseline gap-1.5 text-[13px] px-2.5 py-1 rounded-lg"
                style={{ background: "var(--surface-2)", border: "1px solid var(--line)" }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 2, background: BARVA.nefinancni, display: "inline-block" }} />
                <span style={{ color: "var(--muted)" }}>{druhLabel(d.druh, lang) || d.druh}</span>
                <b className="tabular-nums" style={{ color: "var(--ink)" }}>{fmt(d.castka)}</b>
              </motion.span>
            ))}
          </div>
        </div>
      )}

      {/* Neúplnost se PŘIZNÁVÁ. Bez toho by klient viděl menší jmění, než má,
          a nikdo by nevěděl proč. */}
      {r.bezHodnoty > 0 && (
        <div className="mt-3 text-[13px]" style={{ color: "var(--warn-text)" }} data-testid="rozvaha-neuplne">
          {/* Tvar slova podle počtu dělá pluralJazyka (Intl.PluralRules), ne ruční
              pravidlo 1 / 2–4 / 5+ — to je pravidlo ČEŠTINY. */}
          {t("bezOceneni", {
            pocet: r.bezHodnoty,
            polozka: pluralJazyka(lang, "nastroje.pdVety.polozka", r.bezHodnoty),
          })}
        </div>
      )}
    </div>
  );
}
